import { Component, inject } from '@angular/core';
import { MaterialDesignConfig } from '../../app.config.material';

import { ActivatedRoute, Router } from '@angular/router';
import '../../extension-methods/router.extension';
import { AppMonitoring } from '../../app-services/monitoring/monitor.app';
import { AppPwaLaunch } from '../../app-services/pwa-launch/pwa-launch.app';
import { HOME_QUERY_PARAM } from '../../guards/pwa-launch.guard';
import { PlatformHelper } from '../../helpers/platform.helper';
import { ModelPwaLaunchTarget } from '../../models/pwa/pwa-launch-target.model';

@Component({
    selector: 'app-landing-page',
    imports: [
    MaterialDesignConfig
],
    templateUrl: './landing-page.component.html',
    styleUrl: './landing-page.component.scss'
})
export class LandingPageComponent {

  private readonly appMonitoring = inject(AppMonitoring);
  private readonly appPwaLaunch = inject(AppPwaLaunch);
  private readonly route = inject(ActivatedRoute);

  private launchTarget: ModelPwaLaunchTarget | null = null;

  constructor(private readonly router: Router) {
    // An installed app only reaches the landing page when pwaLaunchGuard let it through: the user
    // asked for it with ?home=1, they navigated home in-app, or there was no campaign to resume.
    // That last case is the norm on iOS, where a Home Screen web app runs in its own storage
    // partition and cannot see the launch target Safari recorded. Marketing copy is the wrong
    // answer for an installed app, so it gets a panel that leads back to a campaign instead.
    this.isStandaloneLaunch =
      PlatformHelper.isStandalone() && this.route.snapshot.queryParamMap.get(HOME_QUERY_PARAM) !== '1';

    if (this.isStandaloneLaunch) {
      this.launchTarget = this.appPwaLaunch.resolveLaunchTarget();
      this.appMonitoring.logEvent(this, 'Installed app opened the landing page', {
        hasCampaignToReturnTo: !!this.launchTarget
      });
    }
  }

  isStandaloneLaunch = false;

  get hasCampaignToReturnTo(): boolean {
    return !!this.launchTarget;
  }

  login() {
    this.router.NavigateTo.SideNav.CampaignList();
  }

  learnMore() {
    // Scroll to features section
    const featuresSection = document.querySelector('.features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /** Reopens the campaign the installed app was last used for, at the step it was left on. */
  returnToCampaign(): void {
    if (!this.launchTarget) {
      return;
    }

    this.router
      .navigate(
        this.appPwaLaunch.buildViewerCommands(this.launchTarget),
        this.appPwaLaunch.buildViewerNavigationExtras(this.launchTarget)
      )
      .catch((error: unknown) => {
        this.appMonitoring.logException(
          this,
          error instanceof Error ? error : 'Failed to reopen the recorded campaign'
        );
      });
  }

  /** Falls through to the marketing page, for anyone who reached the installed-app panel by accident. */
  showLandingPage(): void {
    this.isStandaloneLaunch = false;
  }
}
